import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Available CSS units for dimensions
 */
const UNITS = [
  { value: 'px', label: 'px - Pixels' },
  { value: '%', label: '% - Percentage' },
  { value: 'rem', label: 'rem - Root EM' },
  { value: 'em', label: 'em - EM' },
  { value: 'vh', label: 'vh - Viewport Height' },
  { value: 'vw', label: 'vw - Viewport Width' },
];

/**
 * Compact units for inline display
 */
const COMPACT_UNITS = ['px', '%', 'rem', 'vw', 'vh'];

/**
 * DimensionField - Single dimension input with unit selector
 *
 * Supports:
 * - Numeric value with unit selection
 * - 'auto' option for unconstrained dimension
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {Object} [props.field.default] - Default dimension { value, unit }
 * @param {boolean} [props.field.allowAuto] - Whether to show 'auto' option (default: true)
 * @param {Object} props.value - Current dimension value { value, unit }
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.collapsible] - Whether to show as collapsible (default: true)
 * @param {boolean} [props.defaultCollapsed] - Initial collapsed state (default: false)
 * @param {boolean} [props.compact] - Use compact inline layout (default: false)
 */
export default function DimensionField({
  field,
  value,
  onChange,
  disabled = false,
  collapsible = true,
  defaultCollapsed = false,
  compact = false,
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // Normalize value to dimension object format
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null) {
      return {
        value: val.value ?? 'auto',
        unit: val.unit || 'px'
      };
    }
    return field.default || { value: 'auto', unit: 'px' };
  };

  const dimensionObj = normalizeValue(value);
  const isAuto = dimensionObj.value === 'auto';
  const allowAuto = field.allowAuto !== false; // Default to true

  const updateDimension = (updates) => {
    onChange({ ...dimensionObj, ...updates });
  };

  const toggleAuto = () => {
    updateDimension({ value: isAuto ? 0 : 'auto' });
  };

  // Compact mode: single row with value + unit
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex items-center gap-1 flex-1">
          <input
            type="text"
            inputMode={isAuto ? 'text' : 'numeric'}
            value={isAuto ? 'auto' : (dimensionObj.value === 0 ? '' : dimensionObj.value)}
            placeholder="auto"
            onChange={(e) => {
              const val = e.target.value.trim().toLowerCase();
              if (val === 'a' || val === 'auto') {
                updateDimension({ value: 'auto' });
              } else {
                const numVal = parseFloat(e.target.value);
                updateDimension({ value: isNaN(numVal) ? 0 : numVal });
              }
            }}
            disabled={disabled}
            title={`${field.label} (type "auto" for auto)`}
            className={`w-16 px-2 py-1 text-xs text-center border rounded bg-gray-50
              ${isAuto ? 'text-blue-600 font-medium' : ''}
              border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
          {!isAuto && (
            <select
              value={dimensionObj.unit}
              onChange={(e) => updateDimension({ unit: e.target.value })}
              disabled={disabled}
              className="w-14 px-1 py-1 text-xs border rounded bg-gray-50 border-gray-200
                focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            >
              {COMPACT_UNITS.map((unit) => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          )}
        </div>
      </div>
    );
  }

  const renderContent = () => (
    <div className="space-y-2">
      {/* Auto checkbox */}
      {allowAuto && (
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isAuto}
            onChange={toggleAuto}
            disabled={disabled}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="ml-2 text-xs text-gray-700">Auto (no constraint)</span>
        </label>
      )}

      {/* Value input - disabled when auto */}
      {!isAuto && (
        <>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Value</label>
            <input
              type="number"
              value={dimensionObj.value}
              onChange={(e) => updateDimension({ value: parseFloat(e.target.value) || 0 })}
              disabled={disabled}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="0"
              step="1"
              min="0"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Unit</label>
            <select
              value={dimensionObj.unit}
              onChange={(e) => updateDimension({ unit: e.target.value })}
              disabled={disabled}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              {UNITS.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>

          {/* Preview */}
          <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
            Preview: {dimensionObj.value}{dimensionObj.unit}
          </div>
        </>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {field.label}
        </label>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <label className="text-xs font-medium text-gray-700 cursor-pointer">
          {field.label}
        </label>
        {isCollapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {!isCollapsed && (
        <div className="p-3">
          {renderContent()}
        </div>
      )}
    </div>
  );
}
